import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import axios from 'axios';

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('adminToken')}` };
}

const REQ_ICONS = ['🖥️','🌐','⚙️','🗑️','🔍'];
const STATUS_COLOR = { compliant: '#22c55e', partial: '#f59e0b', non_compliant: '#ef4444' };
const SEV_COLOR    = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#22c55e' };

const STATUS_BADGE = {
  detected:        { cls: 'failed',  label: '⚠ Detected' },
  removal_pending: { cls: 'pending', label: '⏳ Removal Pending' },
  removed:         { cls: 'success', label: '✔ Removed' },
  accepted:        { cls: 'info',    label: '✔ Accepted' },
};

function EolTab({ unsupported, onUpdate }) {
  const [filter, setFilter] = useState('active'); // active | all | removed | accepted

  const filtered = unsupported.filter(u => {
    if (filter === 'active')   return u.status === 'detected' || u.status === 'removal_pending';
    if (filter === 'removed')  return u.status === 'removed';
    if (filter === 'accepted') return u.status === 'accepted';
    return true; // all
  });

  const counts = {
    active:   unsupported.filter(u => u.status === 'detected' || u.status === 'removal_pending').length,
    removed:  unsupported.filter(u => u.status === 'removed').length,
    accepted: unsupported.filter(u => u.status === 'accepted').length,
    all:      unsupported.length,
  };

  return (
    <div className="panel">
      <div className="panel-header">
        <div className="panel-title">
          <div className="panel-title-icon">🗑</div>
          Unsupported / EOL Software
        </div>
        {/* Filter tabs */}
        <div className="flex-gap">
          {[
            ['active',   `Active (${counts.active})`],
            ['removed',  `Removed (${counts.removed})`],
            ['accepted', `Accepted (${counts.accepted})`],
            ['all',      `All (${counts.all})`],
          ].map(([id, label]) => (
            <button
              key={id}
              className={`btn btn-sm ${filter === id ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Info banner for removed/accepted */}
      {(filter === 'removed' || filter === 'accepted') && (
        <div className="alert info" style={{ margin: '12px 16px 0', borderRadius: 8 }}>
          ℹ These items are marked as {filter} and will <strong>not</strong> be re-detected by future scans.
          They are kept here as an audit trail.
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th>Device</th>
            <th>Product</th>
            <th>Version</th>
            <th>Vendor</th>
            <th>EOL Date</th>
            <th>Reason</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr>
              <td colSpan={8} className="empty">
                {filter === 'active'   && 'No active EOL software — all clear ✔'}
                {filter === 'removed'  && 'No removed items yet.'}
                {filter === 'accepted' && 'No accepted items yet.'}
                {filter === 'all'      && 'No EOL software detected.'}
              </td>
            </tr>
          )}
          {filtered.map(u => {
            const badge = STATUS_BADGE[u.status] || { cls: 'pending', label: u.status };
            const isActive = u.status === 'detected' || u.status === 'removal_pending';
            return (
              <tr key={u.id} style={{ opacity: isActive ? 1 : 0.7 }}>
                <td><strong>{u.device?.hostname || '—'}</strong></td>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{u.productName}</div>
                </td>
                <td style={{ fontSize: 12, color: '#94a3b8' }}>{u.version || '—'}</td>
                <td style={{ fontSize: 12 }}>{u.vendor || '—'}</td>
                <td style={{ fontSize: 12, color: '#dc2626' }}>
                  {u.eolDate ? new Date(u.eolDate).toLocaleDateString() : '—'}
                </td>
                <td style={{ fontSize: 11, color: '#94a3b8', maxWidth: 200 }}>{u.reason}</td>
                <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                <td>
                  <div className="flex-gap">
                    {u.status === 'detected' && (
                      <>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => onUpdate(u.id, 'removal_pending')}
                          title="Mark this software for removal"
                        >
                          Mark Removal
                        </button>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => onUpdate(u.id, 'accepted')}
                          title="Accept as known/approved — won't affect compliance"
                        >
                          Accept
                        </button>
                      </>
                    )}
                    {u.status === 'removal_pending' && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => onUpdate(u.id, 'removed')}
                        title="Confirm the software has been removed from the device"
                      >
                        Confirm Removed
                      </button>
                    )}
                    {(u.status === 'removed' || u.status === 'accepted') && (
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        {u.removedAt ? `on ${new Date(u.removedAt).toLocaleDateString()}` : 'Resolved'}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function PatchVulnCompliance({ user }) {
  const [summary, setSummary]       = useState(null);
  const [vulns, setVulns]           = useState([]);
  const [unsupported, setUnsupported] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [tab, setTab]               = useState('overview');
  const [msg, setMsg]               = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, v, u] = await Promise.all([
        axios.get('/api/compliance/summary',       { headers: getHeaders() }),
        axios.get('/api/compliance/vulnerabilities',{ headers: getHeaders() }),
        axios.get('/api/compliance/unsupported',   { headers: getHeaders() })
      ]);
      setSummary(s.data);
      setVulns(v.data);
      setUnsupported(u.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function evaluate() {
    setEvaluating(true);
    try {
      await axios.post('/api/compliance/evaluate', {}, { headers: getHeaders() });
      setMsg('Compliance evaluated for all devices.');
      await load();
    } catch (e) { setMsg('Evaluation failed.'); }
    finally { setEvaluating(false); setTimeout(() => setMsg(''), 4000); }
  }

  async function updateVulnStatus(id, status) {
    await axios.patch(`/api/compliance/vulnerabilities/${id}`, { status }, { headers: getHeaders() });
    setVulns(v => v.map(x => x.id === id ? { ...x, status } : x));
  }

  async function updateUnsupportedStatus(id, status) {
    await axios.patch(`/api/compliance/unsupported/${id}`, { status }, { headers: getHeaders() });
    setUnsupported(u => u.map(x => x.id === id ? { ...x, status } : x));
  }

  if (loading) return <div className="loading">Loading compliance data...</div>;

  const openVulns     = vulns.filter(v => v.status === 'open');
  const criticalVulns = openVulns.filter(v => v.severity === 'critical');
  const activeEol     = unsupported.filter(u => u.status !== 'removed');

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Patch & Vulnerability Management</div>
          <div className="page-subtitle">5-requirement compliance tracking across all devices</div>
        </div>
        <div className="flex-gap">
          <button className="refresh-btn" onClick={load}>↻ Refresh</button>
          {user?.role === 'admin' && (
            <button className="btn btn-primary" onClick={evaluate} disabled={evaluating}>
              {evaluating ? '⟳ Evaluating...' : '▶ Run Evaluation'}
            </button>
          )}
        </div>
      </div>

      {msg && <div style={{ background:'#14532d', color:'#22c55e', padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>}

      {/* Top KPI cards */}
      <div className="cards-row">
        <div className="card blue" style={{ cursor:'pointer' }} onClick={() => setTab('devices')}>
          <div className="card-label">Total Devices</div>
          <div className="card-value">{summary?.totalDevices || 0}</div>
          <div style={{ fontSize:10, opacity:0.7, marginTop:2 }}>click → devices</div>
        </div>
        <div className="card green" style={{ cursor:'pointer' }} onClick={() => setTab('devices')}>
          <div className="card-label">Fully Compliant</div>
          <div className="card-value">{summary?.fullyCompliant || 0}</div>
          <div style={{ fontSize:10, opacity:0.7, marginTop:2 }}>click → devices</div>
        </div>
        <div className="card cyan" style={{ cursor:'pointer' }} onClick={() => setTab('overview')}>
          <div className="card-label">Avg Score</div>
          <div className="card-value">{summary?.avgScore || 0}%</div>
          <div style={{ fontSize:10, opacity:0.7, marginTop:2 }}>click → overview</div>
        </div>
        <div className="card red" style={{ cursor:'pointer' }} onClick={() => setTab('vulns')}>
          <div className="card-label">Open Vulnerabilities</div>
          <div className="card-value">{openVulns.length}</div>
          <div style={{ fontSize:10, opacity:0.7, marginTop:2 }}>click → vulnerabilities</div>
        </div>
        <div className="card red" style={{ cursor:'pointer' }} onClick={() => setTab('vulns')}>
          <div className="card-label">Critical Vulns</div>
          <div className="card-value">{criticalVulns.length}</div>
          <div style={{ fontSize:10, opacity:0.7, marginTop:2 }}>click → vulnerabilities</div>
        </div>
        <div className="card yellow" style={{ cursor:'pointer' }} onClick={() => setTab('eol')}>
          <div className="card-label">EOL Software</div>
          <div className="card-value">{activeEol.length}</div>
          <div style={{ fontSize:10, opacity:0.7, marginTop:2 }}>click → EOL software</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-gap" style={{ marginBottom: 20 }}>
        {[['overview','📋 Overview'],['devices','🖥️ Devices'],['vulns','⚠️ Vulnerabilities'],['eol','🗑️ EOL Software']].map(([id, label]) => (
          <button key={id} className={`btn ${tab === id ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {/* ── Overview Tab ── */}
      {tab === 'overview' && summary && (
        <>
          <div className="table-wrap section-gap" style={{ padding: 24 }}>
            <div className="table-title" style={{ marginBottom: 20 }}>Requirements Status</div>
            {summary.requirements.map((req, i) => (
              <div key={req.id} style={{ marginBottom: 20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 14 }}>{REQ_ICONS[i]} {req.label}</span>
                  <span style={{ fontSize: 13, color: req.pct === 100 ? '#22c55e' : req.pct >= 60 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                    {req.compliant}/{req.total} devices ({req.pct}%)
                  </span>
                </div>
                <div className="progress-bar-wrap">
                  <div
                    className={`progress-bar ${req.pct === 100 ? 'green' : req.pct >= 60 ? '' : 'red'}`}
                    style={{ width: `${req.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Requirement detail cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px,1fr))', gap:16 }}>
            {summary.requirements.map((req, i) => (
              <div key={req.id} className="card" style={{ borderLeft: `3px solid ${req.pct===100?'#22c55e':req.pct>=60?'#f59e0b':'#ef4444'}` }}>
                <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:8 }}>
                  Requirement {i+1}
                </div>
                <div style={{ fontSize:14, fontWeight:600, marginBottom:12 }}>{REQ_ICONS[i]} {req.label}</div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span className={`badge ${req.pct===100?'success':req.pct>=60?'pending':'failed'}`}>
                    {req.pct===100 ? '✔ Compliant' : req.pct>=60 ? '⚠ Partial' : '✘ Non-Compliant'}
                  </span>
                  <span style={{ fontSize:22, fontWeight:700, color: req.pct===100?'#22c55e':req.pct>=60?'#f59e0b':'#ef4444' }}>
                    {req.pct}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Devices Tab ── */}
      {tab === 'devices' && summary && (
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title"><div className="panel-title-icon">🖥</div>Device Compliance Status</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{summary.devices.length} device(s)</div>
          </div>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ minWidth: 860, tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: 140 }} /> {/* Hostname */}
                <col style={{ width: 90  }} /> {/* OS Patch */}
                <col style={{ width: 80  }} /> {/* Browser */}
                <col style={{ width: 80  }} /> {/* Firmware */}
                <col style={{ width: 90  }} /> {/* EOL */}
                <col style={{ width: 110 }} /> {/* Vuln Scan */}
                <col style={{ width: 120 }} /> {/* Score */}
                <col style={{ width: 130 }} /> {/* Status */}
              </colgroup>
              <thead>
                <tr>
                  <th>Hostname</th>
                  <th style={{ textAlign: 'center' }}>🖥 OS Patch</th>
                  <th style={{ textAlign: 'center' }}>🌐 Browser</th>
                  <th style={{ textAlign: 'center' }}>⚙ Firmware</th>
                  <th style={{ textAlign: 'center' }}>🗑 EOL</th>
                  <th style={{ textAlign: 'center' }}>🔍 Vuln Scan</th>
                  <th>Score</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {summary.devices.length === 0 && (
                  <tr><td colSpan={8} className="empty">No compliance data. Click Run Evaluation.</td></tr>
                )}
                {summary.devices.map(r => {
                  const statusColor = r.overallStatus === 'compliant' ? { bg:'#dcfce7', color:'#16a34a' }
                    : r.overallStatus === 'partial' ? { bg:'#fef3c7', color:'#d97706' }
                    : { bg:'#fee2e2', color:'#dc2626' };
                  const scoreColor = r.overallScore === 100 ? '#16a34a' : r.overallScore >= 60 ? '#d97706' : '#dc2626';
                  return (
                    <tr key={r.id}>
                      {/* Hostname */}
                      <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{r.device?.hostname || '—'}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.device?.os || ''}</div>
                      </td>

                      {/* OS Patch */}
                      <td style={{ textAlign: 'center' }}>
                        {r.osPatchCompliant
                          ? <span className="badge success" style={{ fontSize: 11 }}>✔</span>
                          : r.osPatchDaysOverdue === 0
                            ? <span className="badge pending" style={{ fontSize: 10 }}>⏳ Pending</span>
                            : <span className="badge failed" style={{ fontSize: 10 }}>✘ {r.osPatchDaysOverdue}d</span>
                        }
                      </td>

                      {/* Browser */}
                      <td style={{ textAlign: 'center' }}>
                        {r.browserCompliant
                          ? <span className="badge success" style={{ fontSize: 11 }}>✔</span>
                          : <span className="badge failed" style={{ fontSize: 11 }}>✘</span>
                        }
                      </td>

                      {/* Firmware */}
                      <td style={{ textAlign: 'center' }}>
                        {r.firmwareCompliant
                          ? <span className="badge success" style={{ fontSize: 11 }}>✔</span>
                          : <span className="badge failed" style={{ fontSize: 11 }}>✘</span>
                        }
                      </td>

                      {/* EOL */}
                      <td style={{ textAlign: 'center' }}>
                        {r.unsupportedCompliant
                          ? <span className="badge success" style={{ fontSize: 11 }}>✔</span>
                          : <span className="badge failed" style={{ fontSize: 10 }}>✘ {r.unsupportedCount}</span>
                        }
                      </td>

                      {/* Vuln Scan */}
                      <td style={{ textAlign: 'center' }}>
                        {r.vulnScanCompliant
                          ? <span className="badge success" style={{ fontSize: 11 }}>✔</span>
                          : <span className="badge failed" style={{ fontSize: 10 }}>✘ Not scanned</span>
                        }
                      </td>

                      {/* Score — progress bar + % */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{
                            flex: 1, height: 6, background: '#e8ecf4',
                            borderRadius: 3, overflow: 'hidden', minWidth: 40
                          }}>
                            <div style={{
                              height: '100%', width: `${r.overallScore}%`,
                              background: scoreColor, borderRadius: 3,
                              transition: 'width 0.3s'
                            }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor, minWidth: 30 }}>
                            {r.overallScore}%
                          </span>
                        </div>
                      </td>

                      {/* Status badge */}
                      <td>
                        <span style={{
                          display: 'inline-block',
                          fontSize: 11, fontWeight: 700,
                          padding: '3px 10px', borderRadius: 20,
                          background: statusColor.bg, color: statusColor.color,
                          whiteSpace: 'nowrap'
                        }}>
                          {r.overallStatus === 'compliant'    ? '✔ Compliant'
                          : r.overallStatus === 'partial'     ? '⚠ Partial'
                          : '✘ Non-Compliant'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Vulnerabilities Tab ── */}
      {tab === 'vulns' && (
        <div className="table-wrap">
          <div className="table-header">
            <div className="table-title">Vulnerabilities ({openVulns.length} open)</div>
          </div>
          <table>
            <thead>
              <tr><th>Device</th><th>CVE</th><th>Title</th><th>Severity</th><th>Product</th><th>Version</th><th>Status</th><th>Action</th></tr>
            </thead>
            <tbody>
              {vulns.length === 0 && <tr><td colSpan={8} className="empty">No vulnerabilities found. Run a scan from the agent.</td></tr>}
              {vulns.map(v => (
                <tr key={v.id}>
                  <td>{v.device?.hostname || '—'}</td>
                  <td style={{ fontSize:11, color:'#64748b' }}>{v.cveId || '—'}</td>
                  <td style={{ maxWidth:200, fontSize:12 }}>{v.title}</td>
                  <td><span className="badge" style={{ background: v.severity==='critical'?'#450a0a':v.severity==='high'?'#431407':'#422006', color: SEV_COLOR[v.severity] }}>{v.severity}</span></td>
                  <td style={{ fontSize:12 }}>{v.affectedProduct}</td>
                  <td style={{ fontSize:12, color:'#64748b' }}>{v.affectedVersion}</td>
                  <td><span className={`badge ${v.status==='resolved'?'success':v.status==='open'?'failed':'pending'}`}>{v.status}</span></td>
                  <td>
                    {v.status === 'open' && (
                      <div className="flex-gap">
                        <button className="btn btn-sm btn-ghost" onClick={() => updateVulnStatus(v.id, 'mitigated')}>Mitigate</button>
                        <button className="btn btn-sm btn-primary" onClick={() => updateVulnStatus(v.id, 'resolved')}>Resolve</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── EOL Software Tab ── */}
      {tab === 'eol' && (
        <EolTab unsupported={unsupported} onUpdate={updateUnsupportedStatus} />
      )}
    </div>
  );
}
